import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import StopMap, { MAP_COLORS, VAN_SVG } from "./StopMap";
import LandmarkMap, { RADIUS_MAX_M, RADIUS_MIN_M } from "./LandmarkMap";
import LOGO, { LOGO_ALT } from "./logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "stopcheck_app_key";
const TZ = "America/Edmonton";

// Tags are managed in Settings; these are shown until the saved list loads.
const DEFAULT_TAGS = [
  { id: "unclassified", label: "Unclassified", cleaning: true, color: "#e8a317", builtin: true },
  { id: "bus_stop", label: "Bus stop", cleaning: true, color: "#1e3a4c", builtin: false },
  { id: "shelter", label: "Shelter", cleaning: true, color: "#2b6cb0", builtin: false },
  { id: "bench", label: "Bench", cleaning: true, color: "#7b4fa0", builtin: false },
  { id: "not_cleaning", label: "Not a cleaning stop", cleaning: false, color: "#8a9499", builtin: true },
];
const FALLBACK_TAG_COLOR = "#8a9499";

function tagsInfo(list, reload) {
  const labels = {};
  const colors = {};
  list.forEach((t) => {
    labels[t.id] = t.label;
    colors[t.id] = t.color || FALLBACK_TAG_COLOR;
  });
  return {
    list,
    labels,
    colors,
    ids: list.map((t) => t.id),
    cleaningIds: list.filter((t) => t.cleaning).map((t) => t.id),
    reload,
  };
}

const TagsContext = createContext(tagsInfo(DEFAULT_TAGS, () => {}));

function useTags() {
  return useContext(TagsContext);
}

function formatCoords(lat, lng) {
  return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
}

// "52.2681, -113.8112" (or separated by spaces or ;) -> { lat, lng }, else null.
function parseCoords(text) {
  const parts = String(text || "").trim().split(/[\s,;]+/).filter(Boolean);
  if (parts.length !== 2) return null;
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
}

// Where a stop stands on the report day.
const STATUS_INFO = {
  needs_cleaning: { label: "Needs cleaning", tone: "bad" },
  cleaned: { label: "Cleaned", tone: "ok" },
  still_clean: { label: "Still clean", tone: "calm" },
};
const SUMMARY_ORDER = ["cleaned", "still_clean", "needs_cleaning"];

// What the GPS saw at a stop (a single visit, or the best visit of the day).
const VISIT_INFO = {
  cleaned: { label: "Cleaned", tone: "ok" },
  short: { label: "Too short", tone: "warn" },
  not_visited: { label: "Not visited", tone: "muted" },
};

const VERDICTS = {
  good: { label: "Good", note: "A point every minute or less", tone: "ok" },
  usable: { label: "Usable", note: "A point every 1 to 2 minutes", tone: "warn" },
  coarse: { label: "Too sparse", note: "Gaps over 2 minutes", tone: "bad" },
  not_enough_data: { label: "Not enough data", note: "Fewer than 5 moving gaps", tone: "muted" },
};

function formatTime(value) {
  if (!value) return "Never";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Never";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  }).format(d);
}

function formatClock(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: TZ, hour: "numeric", minute: "2-digit" }).format(d);
}

function formatDuration(seconds) {
  if (seconds == null) return "";
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s} s`;
  return s ? `${m} min ${s} s` : `${m} min`;
}

function localToday() {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type).value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function shiftDate(dateStr, days) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

function formatDay(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Intl.DateTimeFormat("en-CA", { timeZone: "UTC", weekday: "long", month: "long", day: "numeric", year: "numeric" })
    .format(new Date(Date.UTC(y, m - 1, d)));
}

function formatShortDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Intl.DateTimeFormat("en-CA", { timeZone: "UTC", month: "short", day: "numeric" })
    .format(new Date(Date.UTC(y, m - 1, d)));
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

function describeUpkeep(r) {
  if (r.status === "needs_cleaning") {
    if (!r.last_cleaned) return "No cleaning on record";
    return `Overdue ${plural(r.days_overdue, "day")}, last cleaned ${formatShortDate(r.last_cleaned)}`;
  }
  if (r.status === "still_clean") {
    if (r.days_left === 0) return `Clean until ${formatShortDate(r.clean_until)} (last day)`;
    return `Clean until ${formatShortDate(r.clean_until)} (${plural(r.days_left, "day")} left)`;
  }
  return r.clean_until ? `Clean until ${formatShortDate(r.clean_until)}` : "";
}

function formatNumber(n) {
  return typeof n === "number" ? n.toLocaleString("en-CA") : "0";
}

async function apiFetch(path, appKey, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "X-App-Key": appKey,
      ...(options.headers || {}),
    },
  });
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }
  if (!res.ok) {
    const err = new Error((body && body.detail) || `Request failed with status ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return body;
}

// File upload: the browser sets the multipart Content-Type itself.
async function apiUpload(path, appKey, file) {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API}${path}`, { method: "POST", headers: { "X-App-Key": appKey }, body: form });
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }
  if (!res.ok) {
    const err = new Error((body && body.detail) || `Request failed with status ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return body;
}

function Badge({ tone, children }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

function KeyGate({ onSubmit, error }) {
  const [value, setValue] = useState("");
  return (
    <div className="gate">
      <div className="gate-panel">
        <img className="gate-logo" src={LOGO} alt={LOGO_ALT} />
        <h1 className="gate-title">Stop Check</h1>
        <p className="gate-copy">Enter the access key to view GPS collection and stop landmarks.</p>
        <label className="field-label" htmlFor="appkey">Access key</label>
        <input
          id="appkey"
          type="password"
          className="input"
          value={value}
          autoFocus
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && value.trim()) onSubmit(value.trim());
          }}
        />
        {error ? <p className="error-text">{error}</p> : null}
        <button className="btn btn-primary gate-btn" disabled={!value.trim()} onClick={() => onSubmit(value.trim())}>
          Open dashboard
        </button>
      </div>
    </div>
  );
}

function StatusView({ appKey, onAuthError, onShowReport }) {
  const { labels: tagLabels, ids: tagIds } = useTags();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [sample, setSample] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoMessage, setDemoMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch("/status", appKey);
      setStatus(data);
      setError("");
    } catch (e) {
      if (e.status === 401) onAuthError(e.message);
      else setError(e.message);
    }
  }, [appKey, onAuthError]);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const startPull = async (withLandmarks) => {
    setMessage("");
    try {
      const res = await apiFetch(`/collector/run?sync_landmarks=${withLandmarks}`, appKey, { method: "POST" });
      setMessage(res.started ? "Pull started. This page refreshes every 15 seconds." : res.reason);
      load();
    } catch (e) {
      setMessage(e.message);
    }
  };

  const addDemo = async () => {
    setDemoBusy(true);
    setDemoMessage(null);
    try {
      const res = await apiFetch("/demo/seed", appKey, { method: "POST" });
      const counts = res.plan.reduce((acc, item) => ({ ...acc, [item.kind]: (acc[item.kind] || 0) + 1 }), {});
      setDemoMessage({ date: res.date, text: `Added a simulated morning on ${formatDay(res.date)}: ${counts.cleaned || 0} stops cleaned, ${counts.short || 0} stays too short, and ${counts.drive_by || 0} drive-bys.` });
      load();
    } catch (e) {
      setDemoMessage({ text: e.message });
    } finally {
      setDemoBusy(false);
    }
  };

  const removeDemo = async () => {
    setDemoBusy(true);
    try {
      const res = await apiFetch("/demo", appKey, { method: "DELETE" });
      setDemoMessage({ text: `Removed ${formatNumber(res.points_removed)} simulated points and ${formatNumber(res.visits_removed)} visits.` });
      load();
    } catch (e) {
      setDemoMessage({ text: e.message });
    } finally {
      setDemoBusy(false);
    }
  };

  const toggleSample = async () => {
    if (sample) {
      setSample(null);
      return;
    }
    try {
      setSample(await apiFetch("/titan/sample", appKey));
    } catch (e) {
      setMessage(e.message);
    }
  };

  if (error) return <p className="error-box">{error}</p>;
  if (!status) return <p className="muted">Loading status…</p>;

  const collector = status.collector || {};
  const lastRun = collector.last_run || {};
  const lmSync = status.landmark_sync || {};
  const classes = status.landmarks_by_classification || {};
  const activeLandmarks = Object.values(classes).reduce((a, b) => a + b, 0);
  const landmarksLocal = status.landmark_source === "local";

  let connection = { tone: "muted", label: "Not configured" };
  if (status.titan_configured) {
    if (status.pull_running) connection = { tone: "warn", label: "Pulling data" };
    else if (collector.last_error && lastRun.error) connection = { tone: "bad", label: "Last pull failed" };
    else if (collector.last_success_at) connection = { tone: "ok", label: "Connected" };
    else connection = { tone: "warn", label: "Waiting for first pull" };
  }

  return (
    <div className="stack">
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">Titan GPS connection</h2>
          <Badge tone={connection.tone}>{connection.label}</Badge>
        </div>
        {!status.titan_configured ? (
          <p className="muted">
            Add TITAN_ACCESS_KEY and TITAN_TOKEN to the backend settings, then restart the backend.
          </p>
        ) : null}
        <dl className="facts">
          <div><dt>Collected up to</dt><dd>{formatTime(collector.cursor)}</dd></div>
          <div><dt>Last successful pull</dt><dd>{formatTime(collector.last_success_at)}</dd></div>
          <div><dt>{landmarksLocal ? "Landmarks" : "Last landmark sync"}</dt><dd>{landmarksLocal ? "Managed in Stop Check" : formatTime(lmSync.last_sync_at)}</dd></div>
          <div><dt>Last run</dt><dd>{lastRun.calls != null ? `${lastRun.calls} calls, ${formatNumber(lastRun.inserted)} new points` : "None yet"}</dd></div>
        </dl>
        {lastRun.error ? <p className="error-box">Last pull stopped: {lastRun.error}</p> : null}
        {lmSync.last_error && !landmarksLocal ? <p className="error-box">Landmark sync: {lmSync.last_error}</p> : null}
        <div className="actions">
          <button className="btn btn-primary" disabled={!status.titan_configured || status.pull_running} onClick={() => startPull(false)}>
            Pull GPS data now
          </button>
          {!landmarksLocal ? (
            <button className="btn" disabled={!status.titan_configured || status.pull_running} onClick={() => startPull(true)}>
              Pull GPS data and landmarks
            </button>
          ) : null}
        </div>
        {message ? <p className="note">{message}</p> : null}
      </section>

      <div className="grid-2">
        <section className="panel">
          <h2 className="panel-title">GPS points</h2>
          <p className="big-number">{formatNumber(status.points_24h)}</p>
          <p className="muted">in the last 24 hours, {formatNumber(status.points_total)} stored in total</p>
        </section>
        <section className="panel">
          <h2 className="panel-title">Landmarks</h2>
          <p className="big-number">{formatNumber(activeLandmarks)}</p>
          <p className="muted">
            {formatNumber(classes.unclassified)} unclassified, {formatNumber(status.landmarks_flagged)} with problems
            {status.landmarks_removed ? `, ${formatNumber(status.landmarks_removed)} removed from Titan` : ""}
          </p>
          <ul className="class-list">
            {tagIds.map((key) => (
              <li key={key}><span>{tagLabels[key]}</span><span className="num">{formatNumber(classes[key])}</span></li>
            ))}
          </ul>
        </section>
      </div>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2 className="panel-title">Simulated test day</h2>
            <p className="muted">
              Drives a pretend unit past 12 real stops so you can try the report before vehicles report. It is labelled
              Demo unit (simulated) everywhere and can be removed at any time.
            </p>
          </div>
        </div>
        <div className="actions">
          <button className="btn" disabled={demoBusy} onClick={addDemo}>{status.demo_points ? "Replace simulated day" : "Add simulated day"}</button>
          {status.demo_points ? <button className="btn" disabled={demoBusy} onClick={removeDemo}>Remove simulated data</button> : null}
        </div>
        {demoBusy ? <p className="note">Working…</p> : null}
        {demoMessage ? (
          <p className="note">
            {demoMessage.text}{" "}
            {demoMessage.date ? <button className="link-btn" onClick={() => onShowReport(demoMessage.date)}>Open that day's report</button> : null}
          </p>
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2 className="panel-title">Raw Titan records</h2>
            <p className="muted">One landmark and the newest GPS point exactly as Titan sent them. Useful for checking field names.</p>
          </div>
          <button className="btn" onClick={toggleSample}>{sample ? "Hide records" : "Show records"}</button>
        </div>
        {sample ? <pre className="raw">{JSON.stringify(sample, null, 2)}</pre> : null}
      </section>
    </div>
  );
}

function PointsTable({ appKey, deviceId }) {
  const [points, setPoints] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/points?device_id=${encodeURIComponent(deviceId)}&limit=50`, appKey)
      .then((res) => { if (!cancelled) setPoints(res.points); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [appKey, deviceId]);

  if (error) return <p className="error-box">{error}</p>;
  if (!points) return <p className="muted">Loading points…</p>;
  if (!points.length) return <p className="muted">No points stored for this unit.</p>;

  return (
    <div className="table-wrap">
      <table className="table table-compact">
        <thead>
          <tr><th>Time</th><th className="num">Speed km/h</th><th>Ignition</th><th>Inside landmark</th><th>Event</th><th className="num">Lat, lng</th></tr>
        </thead>
        <tbody>
          {points.map((p) => (
            <tr key={p._id}>
              <td>{formatTime(p.event_time)}</td>
              <td className="num">{p.speed_kph != null ? p.speed_kph : ""}</td>
              <td>{p.ignition_on == null ? "" : p.ignition_on ? "On" : "Off"}</td>
              <td>{p.inside_landmark || ""}</td>
              <td>{p.event_type || ""}</td>
              <td className="num">{p.lat.toFixed(5)}, {p.lng.toFixed(5)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UnitsView({ appKey, onAuthError }) {
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    apiFetch(`/devices?hours=${hours}`, appKey)
      .then((res) => { if (!cancelled) { setData(res.devices); setError(""); } })
      .catch((e) => {
        if (cancelled) return;
        if (e.status === 401) onAuthError(e.message);
        else setError(e.message);
      });
    return () => { cancelled = true; };
  }, [appKey, hours, onAuthError]);

  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2 className="panel-title">How often each unit reports</h2>
          <p className="muted">The 5-minute rule needs a position at least every 1 to 2 minutes. Parked gaps over 30 minutes are ignored.</p>
        </div>
        <select className="input input-inline" value={hours} onChange={(e) => setHours(Number(e.target.value))} aria-label="Time window">
          <option value={24}>Last 24 hours</option>
          <option value={72}>Last 3 days</option>
          <option value={168}>Last 7 days</option>
        </select>
      </div>
      {error ? <p className="error-box">{error}</p> : null}
      {!data && !error ? <p className="muted">Loading units…</p> : null}
      {data && !data.length ? <p className="muted">No GPS points in this window yet. Start a pull from the Status tab.</p> : null}
      {data && data.length ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Unit</th><th className="num">Points</th><th>Last seen</th><th className="num">Typical gap</th><th className="num">Gaps under 1 min</th><th>Reporting</th></tr>
            </thead>
            <tbody>
              {data.map((d) => {
                const verdict = VERDICTS[d.verdict] || VERDICTS.not_enough_data;
                const isOpen = open === d.device_id;
                return [
                  <tr key={d.device_id} className="row-click" onClick={() => setOpen(isOpen ? null : d.device_id)}>
                    <td><button className="link-btn" aria-expanded={isOpen}>{d.device_name}</button></td>
                    <td className="num">{formatNumber(d.points)}</td>
                    <td>{formatTime(d.last)}</td>
                    <td className="num">{d.median_gap_s != null ? `${Math.round(d.median_gap_s)} s` : ""}</td>
                    <td className="num">{d.share_le_60s != null ? `${Math.round(d.share_le_60s * 100)}%` : ""}</td>
                    <td><Badge tone={verdict.tone}>{verdict.label}</Badge> <span className="muted small">{verdict.note}</span></td>
                  </tr>,
                  isOpen ? (
                    <tr key={`${d.device_id}-points`} className="row-detail">
                      <td colSpan={6}><PointsTable appKey={appKey} deviceId={d.device_id} /></td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function StopHistory({ appKey, stopId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/stops/${encodeURIComponent(stopId)}/visits?limit=30`, appKey)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [appKey, stopId]);

  if (error) return <p className="error-box">{error}</p>;
  if (!data) return <p className="muted">Loading visits…</p>;
  if (!data.visits.length) return <p className="muted">No recorded stops here yet.</p>;

  return (
    <div className="table-wrap">
      <p className="muted small history-title">Last {data.visits.length} recorded stops at {data.stop.name}</p>
      <table className="table table-compact">
        <thead>
          <tr><th>Arrived</th><th>Unit</th><th className="num">Stayed</th><th>Result</th><th className="num">Closest</th></tr>
        </thead>
        <tbody>
          {data.visits.map((v) => {
            const info = VISIT_INFO[v.status] || VISIT_INFO.short;
            return (
              <tr key={v.id}>
                <td>{formatTime(v.start)}</td>
                <td>{v.device_name}</td>
                <td className="num">{formatDuration(v.duration_s)}</td>
                <td><Badge tone={info.tone}>{info.label}</Badge>{v.open ? <span className="muted small"> still there</span> : null}</td>
                <td className="num">{v.min_distance_m != null ? `${Math.round(v.min_distance_m)} m` : ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const DEFAULT_RULE_DRAFT = { radius_m: "30", min_dwell_minutes: "5", stays_clean_days: "7" };

function rulesToDraft(rules, ruleTags) {
  const draft = {};
  ruleTags.forEach((tag) => {
    const rule = (rules && rules[tag]) || { radius_m: 30, min_dwell_minutes: 5, stays_clean_days: 7 };
    draft[tag] = {
      radius_m: String(rule.radius_m),
      min_dwell_minutes: String(rule.min_dwell_minutes),
      stays_clean_days: String(rule.stays_clean_days == null ? 7 : rule.stays_clean_days),
    };
  });
  return draft;
}

function rulesSummary(rules) {
  const values = Object.values(rules || {}).filter(Boolean);
  const same = values.every((r) => r.radius_m === values[0].radius_m &&
    r.min_dwell_minutes === values[0].min_dwell_minutes && r.stays_clean_days === values[0].stays_clean_days);
  if (values.length && same) {
    return `within ${values[0].radius_m} m for at least ${values[0].min_dwell_minutes} min, stays clean ${plural(values[0].stays_clean_days, "day")}, for every tag`;
  }
  return "set separately for each tag";
}

function RulesPanel({ appKey, rules, ruleTags, onSaved }) {
  const { labels: tagLabels } = useTags();
  const rulesKey = JSON.stringify([rules, ruleTags]);
  const [draft, setDraft] = useState(() => rulesToDraft(rules, ruleTags));
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const [savedRules, savedTags] = JSON.parse(rulesKey);
    setDraft(rulesToDraft(savedRules, savedTags));
  }, [rulesKey]);

  const setValue = (tag, field, value) => {
    setDraft((d) => ({ ...d, [tag]: { ...(d[tag] || DEFAULT_RULE_DRAFT), [field]: value } }));
  };

  const save = async () => {
    setBusy(true);
    setMessage("");
    try {
      const byTag = {};
      ruleTags.forEach((tag) => {
        const row = draft[tag] || DEFAULT_RULE_DRAFT;
        byTag[tag] = {
          radius_m: Number(row.radius_m),
          min_dwell_minutes: Number(row.min_dwell_minutes),
          stays_clean_days: Number(row.stays_clean_days),
        };
      });
      await apiFetch("/rules", appKey, {
        method: "PUT",
        body: JSON.stringify({ by_tag: byTag, recompute_days: 7 }),
      });
      setMessage("Saved. The last 7 days are being recalculated; refresh the report in a minute.");
      onSaved();
    } catch (e) {
      setMessage(e.message);
    } finally {
      setBusy(false);
    }
  };

  const recalc = async () => {
    setBusy(true);
    setMessage("");
    try {
      const res = await apiFetch("/visits/recompute", appKey, { method: "POST", body: JSON.stringify({ days: 7 }) });
      setMessage(res.started ? "Recalculating the last 7 days. Refresh the report in a minute." : res.reason);
    } catch (e) {
      setMessage(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel">
      <h2 className="panel-title">Cleaning rules</h2>
      <p className="muted small rules-current">Now: {rulesSummary(rules)}.</p>
      <p className="muted rules-copy">
        Each tag has its own distance from the stop, minimum time, and number of days a stop stays clean. A stop
        cleaned on Monday that stays clean for 4 days is still clean through Friday and needs cleaning on Saturday;
        0 means it needs cleaning every day. A stay of 1 minute or more that falls short of the minimum time shows as
        too short; brief pass-bys are not recorded. Changing a distance or time, or re-tagging landmarks, applies to
        past days after recalculating. Stops marked cleaned by hand stay cleaned.
      </p>
      <div className="table-wrap">
        <table className="table rules-table">
          <thead>
            <tr><th>Tag</th><th>Distance from stop (metres, 10–150)</th><th>Minimum time (minutes, 1–60)</th><th>Stays clean for (days, 0–365)</th></tr>
          </thead>
          <tbody>
            {ruleTags.map((tag) => {
              const row = draft[tag] || DEFAULT_RULE_DRAFT;
              const label = tagLabels[tag] || tag;
              return (
              <tr key={tag}>
                <td className="rules-tag">{label}</td>
                <td>
                  <input
                    className="input rules-input"
                    type="number"
                    min="10"
                    max="150"
                    value={row.radius_m}
                    onChange={(e) => setValue(tag, "radius_m", e.target.value)}
                    aria-label={`${label} distance in metres`}
                  />
                </td>
                <td>
                  <input
                    className="input rules-input"
                    type="number"
                    min="1"
                    max="60"
                    step="0.5"
                    value={row.min_dwell_minutes}
                    onChange={(e) => setValue(tag, "min_dwell_minutes", e.target.value)}
                    aria-label={`${label} minimum minutes`}
                  />
                </td>
                <td>
                  <input
                    className="input rules-input"
                    type="number"
                    min="0"
                    max="365"
                    step="1"
                    value={row.stays_clean_days}
                    onChange={(e) => setValue(tag, "stays_clean_days", e.target.value)}
                    aria-label={`${label} days it stays clean`}
                  />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="actions">
        <button className="btn btn-primary" disabled={busy} onClick={save}>Save and recalculate</button>
        <button className="btn" disabled={busy} onClick={recalc}>Recalculate last 7 days</button>
      </div>
      {message ? <p className="note">{message}</p> : null}
    </section>
  );
}

const REPORT_PAGE_SIZES = [10, 20, 50, 100];
const REPORT_PAGE_SIZE_STORAGE = "stopcheck_report_page_size";

function initialReportPageSize() {
  try {
    const saved = Number(window.localStorage.getItem(REPORT_PAGE_SIZE_STORAGE));
    if (REPORT_PAGE_SIZES.includes(saved)) return saved;
  } catch (e) {
    // storage unavailable: use the default
  }
  return 20;
}

const MAP_COLOR_STORAGE = "stopcheck_report_map_color";

function initialMapColorBy() {
  try {
    if (window.localStorage.getItem(MAP_COLOR_STORAGE) === "tag") return "tag";
  } catch (e) {
    // storage unavailable: use the default
  }
  return "status";
}

const ROUTE_STOPS_STORAGE = "stopcheck_route_stops";
const LIVE_REFRESH_MS = 60000;

function initialShowRouteStops() {
  try {
    return window.localStorage.getItem(ROUTE_STOPS_STORAGE) !== "off";
  } catch (e) {
    return true;
  }
}

function RouteStopSwatch() {
  return (
    <svg className="legend-icon" viewBox="0 0 16 14" width="14" height="12" aria-hidden="true">
      <path d="M8 1 L15 13 H1 Z" fill={MAP_COLORS.route_stop} stroke={MAP_COLORS.route} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function VanSwatch() {
  return <span className="legend-icon legend-van" aria-hidden="true" dangerouslySetInnerHTML={{ __html: VAN_SVG }} />;
}

function ReportView({ appKey, onAuthError, date, onDateChange, onOpenSettings }) {
  const { labels: tagLabels, colors: tagColors, cleaningIds } = useTags();
  const [mapColorBy, setMapColorBy] = useState(initialMapColorBy);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialReportPageSize);
  const [openStop, setOpenStop] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [trackUnits, setTrackUnits] = useState([]);
  const [trackUnit, setTrackUnit] = useState("");
  const [track, setTrack] = useState(null);
  const [trackError, setTrackError] = useState("");
  const [showRouteStops, setShowRouteStops] = useState(initialShowRouteStops);
  const [live, setLive] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [markNote, setMarkNote] = useState("");
  const [markBusy, setMarkBusy] = useState(false);
  const [markMessage, setMarkMessage] = useState("");

  useEffect(() => {
    setSelected(new Set());
    setMarkMessage("");
  }, [date]);

  useEffect(() => {
    let cancelled = false;
    setReport(null);
    setError("");
    apiFetch(`/report/daily?date=${date}`, appKey)
      .then((res) => {
        if (!cancelled) setReport(res);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e.status === 401) onAuthError(e.message);
        else setError(e.message);
      });
    return () => { cancelled = true; };
  }, [appKey, date, reloadToken, onAuthError]);

  useEffect(() => {
    setPage(0);
    setOpenStop(null);
  }, [date, statusFilter, tagFilter, search, pageSize]);

  const changeMapColorBy = (value) => {
    setMapColorBy(value);
    try {
      window.localStorage.setItem(MAP_COLOR_STORAGE, value);
    } catch (e) {
      // storage unavailable: keep the choice for this visit only
    }
  };

  const changePageSize = (value) => {
    const size = Number(value);
    setPageSize(size);
    try {
      window.localStorage.setItem(REPORT_PAGE_SIZE_STORAGE, String(size));
    } catch (e) {
      // storage unavailable: keep the choice for this visit only
    }
  };

  useEffect(() => {
    let cancelled = false;
    setTrackUnits([]);
    setTrackUnit("");
    apiFetch(`/tracks/units?date=${date}`, appKey)
      .then((res) => { if (!cancelled) setTrackUnits(res.units); })
      .catch(() => { if (!cancelled) setTrackUnits([]); });
    return () => { cancelled = true; };
  }, [appKey, date, reloadToken]);

  useEffect(() => {
    let cancelled = false;
    setTrack(null);
    setTrackError("");
    if (!trackUnit) return undefined;
    apiFetch(`/tracks?date=${date}&device_id=${encodeURIComponent(trackUnit)}`, appKey)
      .then((res) => { if (!cancelled) setTrack(res); })
      .catch((e) => { if (!cancelled) setTrackError(e.message); });
    return () => { cancelled = true; };
  }, [appKey, date, trackUnit]);

  const liveOn = showMap && date === localToday();

  useEffect(() => {
    if (!liveOn) {
      setLive(null);
      return undefined;
    }
    let cancelled = false;
    let busy = false;
    const load = () => {
      if (busy || (typeof document !== "undefined" && document.hidden)) return;
      busy = true;
      apiFetch("/live", appKey)
        .then((res) => { if (!cancelled) setLive(res.units || []); })
        .catch(() => { /* keep the last positions; try again next minute */ })
        .finally(() => { busy = false; });
    };
    load();
    const id = setInterval(load, LIVE_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [appKey, liveOn]);

  const changeShowRouteStops = (value) => {
    setShowRouteStops(value);
    try {
      window.localStorage.setItem(ROUTE_STOPS_STORAGE, value ? "on" : "off");
    } catch (e) {
      // storage unavailable: keep the choice for this visit only
    }
  };

  const rows = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    return report.stops.filter((r) =>
      (!statusFilter ||
        (statusFilter === "manual" ? Boolean(r.manual)
          : statusFilter === "short" ? r.gps_status === "short" && r.status !== "cleaned"
            : r.status === statusFilter)) &&
      (!tagFilter || r.classification === tagFilter) &&
      (!q || (r.name || "").toLowerCase().includes(q))
    );
  }, [report, statusFilter, tagFilter, search]);

  const showVisits = useCallback((stopId) => {
    const index = rows.findIndex((r) => r.stop_id === stopId);
    if (index < 0) return;
    setPage(Math.floor(index / pageSize));
    setOpenStop(stopId);
    setTimeout(() => {
      const row = document.getElementById(`stop-row-${stopId}`);
      if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  }, [rows, pageSize]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/report/daily.csv?date=${report.date}`, { headers: { "X-App-Key": appKey } });
      if (!res.ok) throw new Error(`Export failed with status ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stop-check-${report.date}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  const toggleSelected = (stopId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(stopId)) next.delete(stopId);
      else next.add(stopId);
      return next;
    });
  };

  const selectedMarked = report ? report.stops.filter((r) => r.manual && selected.has(r.stop_id)).map((r) => r.stop_id) : [];

  const markCleaned = async () => {
    setMarkBusy(true);
    setMarkMessage("");
    try {
      const res = await apiFetch("/report/manual", appKey, {
        method: "POST",
        body: JSON.stringify({ date, stop_ids: Array.from(selected), note: markNote }),
      });
      setMarkMessage(`Marked ${formatNumber(res.marked)} stop${res.marked === 1 ? "" : "s"} cleaned by hand.`);
      setSelected(new Set());
      setMarkNote("");
      setReloadToken((t) => t + 1);
    } catch (e) {
      if (e.status === 401) onAuthError(e.message);
      else setMarkMessage(e.message);
    } finally {
      setMarkBusy(false);
    }
  };

  const removeMarks = async () => {
    setMarkBusy(true);
    setMarkMessage("");
    try {
      const res = await apiFetch("/report/manual/remove", appKey, {
        method: "POST",
        body: JSON.stringify({ date, stop_ids: selectedMarked }),
      });
      setMarkMessage(`Removed the hand mark from ${formatNumber(res.removed)} stop${res.removed === 1 ? "" : "s"}. They show their GPS result again.`);
      setSelected(new Set());
      setReloadToken((t) => t + 1);
    } catch (e) {
      if (e.status === 401) onAuthError(e.message);
      else setMarkMessage(e.message);
    } finally {
      setMarkBusy(false);
    }
  };

  const today = report ? report.today : null;
  const pageRows = rows.slice(page * pageSize, (page + 1) * pageSize);
  const pageIds = pageRows.map((r) => r.stop_id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const lastPage = Math.max(0, Math.ceil(rows.length / pageSize) - 1);
  const summary = report ? report.summary : null;
  const running = report && report.visits_state && report.visits_state.recompute_running;

  return (
    <div className="stack">
      <section className="panel">
        <div className="panel-head">
          <div>
            <h2 className="panel-title">{formatDay(date)}</h2>
            <p className="muted">
              {report ? `GPS collected up to ${formatTime(report.data_through)}` : "Loading…"}
              {running ? " Recalculating visits…" : ""}
            </p>
          </div>
          <div className="actions actions-tight">
            <button className="btn" onClick={() => onDateChange(shiftDate(date, -1))}>Previous day</button>
            <input
              className="input input-inline"
              type="date"
              value={date}
              max={today || undefined}
              onChange={(e) => { if (e.target.value) onDateChange(e.target.value); }}
              aria-label="Report date"
            />
            <button className="btn" disabled={Boolean(today && date >= today)} onClick={() => onDateChange(shiftDate(date, 1))}>Next day</button>
          </div>
        </div>

        {error ? <p className="error-box">{error}</p> : null}

        {summary ? (
          <div className="summary">
            {SUMMARY_ORDER.map((key) => (
              <button
                key={key}
                className={`summary-item summary-${key} ${statusFilter === key ? "summary-active" : ""}`}
                aria-pressed={statusFilter === key}
                onClick={() => setStatusFilter(statusFilter === key ? "" : key)}
              >
                <span className="summary-number">{formatNumber(summary[key])}</span>
                <span className="summary-label">{STATUS_INFO[key].label}</span>
                {key === "cleaned" && summary.manual ? (
                  <span className="summary-sub">incl. {formatNumber(summary.manual)} marked by hand</span>
                ) : null}
                {key === "cleaned" && summary.linked ? (
                  <span className="summary-sub">incl. {formatNumber(summary.linked)} through a linked landmark</span>
                ) : null}
                {key === "needs_cleaning" && summary.never_cleaned ? (
                  <span className="summary-sub">{formatNumber(summary.never_cleaned)} with no cleaning on record</span>
                ) : null}
              </button>
            ))}
            <div className="summary-total muted">
              of {formatNumber(summary.total)} stops
              {summary.short ? <span className="summary-sub-line">{formatNumber(summary.short)} stopped too briefly this day</span> : null}
            </div>
          </div>
        ) : null}

        {report ? (
          <>
            <div className="filters">
              <div className="filter">
                <label className="field-label" htmlFor="rp-q">Stop</label>
                <input id="rp-q" className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search names" />
              </div>
              <div className="filter">
                <label className="field-label" htmlFor="rp-status">Status</label>
                <select id="rp-status" className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All statuses</option>
                  {SUMMARY_ORDER.map((k) => <option key={k} value={k}>{STATUS_INFO[k].label}</option>)}
                  <option value="short">Stopped too briefly</option>
                  <option value="manual">Marked by hand</option>
                </select>
              </div>
              <div className="filter">
                <label className="field-label" htmlFor="rp-tag">Tag</label>
                <select id="rp-tag" className="input" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
                  <option value="">All tags</option>
                  {cleaningIds.map((c) => <option key={c} value={c}>{tagLabels[c]}</option>)}
                </select>
              </div>
              <div className="filter filter-end">
                <button className="btn" disabled={exporting} onClick={exportCsv}>{exporting ? "Exporting…" : "Export CSV"}</button>
                <button className="btn" onClick={() => setReloadToken((t) => t + 1)}>Refresh</button>
              </div>
            </div>

            <div className="map-block">
              <div className="map-bar">
                <button className="btn" onClick={() => setShowMap((v) => !v)} aria-expanded={showMap}>
                  {showMap ? "Hide map" : "Show map"}
                </button>
                {showMap ? (
                  <>
                    <label className="field-label map-route-label" htmlFor="rp-route">Route</label>
                    <select id="rp-route" className="input input-inline" value={trackUnit} onChange={(e) => setTrackUnit(e.target.value)}>
                      <option value="">No route</option>
                      {trackUnits.map((u) => <option key={u.device_id} value={u.device_id}>{u.device_name}</option>)}
                    </select>
                    {track ? (
                      <label className="check-inline map-check">
                        <input type="checkbox" checked={showRouteStops} onChange={(e) => changeShowRouteStops(e.target.checked)} />
                        Show stops{track.stops ? ` (${formatNumber(track.stops.length)})` : ""}
                      </label>
                    ) : null}
                    <label className="field-label map-route-label" htmlFor="rp-colorby">Colour by</label>
                    <select id="rp-colorby" className="input input-inline" value={mapColorBy} onChange={(e) => changeMapColorBy(e.target.value)}>
                      <option value="status">Status</option>
                      <option value="tag">Tag</option>
                    </select>
                    {mapColorBy === "tag" ? (
                      <ul className="legend" aria-label="Map legend">
                        {cleaningIds.map((c) => <li key={c}><span className="dot" style={{ background: tagColors[c] }} />{tagLabels[c]}</li>)}
                        <li className="legend-note">Larger dots need cleaning or were cleaned; small dots are still clean</li>
                        {track ? <li><span className="line" style={{ background: MAP_COLORS.route }} />Route</li> : null}
                        {track && showRouteStops ? <li><RouteStopSwatch />Stop on route</li> : null}
                        {live && live.length ? <li><VanSwatch />Live location</li> : null}
                      </ul>
                    ) : (
                      <ul className="legend" aria-label="Map legend">
                        <li><span className="dot" style={{ background: MAP_COLORS.needs_cleaning }} />Needs cleaning</li>
                        <li><span className="dot" style={{ background: MAP_COLORS.cleaned }} />Cleaned</li>
                        <li><span className="dot dot-small" style={{ background: MAP_COLORS.still_clean }} />Still clean</li>
                        {track ? <li><span className="line" style={{ background: MAP_COLORS.route }} />Route</li> : null}
                        {track && showRouteStops ? <li><RouteStopSwatch />Stop on route</li> : null}
                        {live && live.length ? <li><VanSwatch />Live location</li> : null}
                      </ul>
                    )}
                  </>
                ) : null}
              </div>
              {showMap && !trackUnits.length ? <p className="muted small">No unit reported GPS on this day, so there is no route to show.</p> : null}
              {trackError ? <p className="error-box">{trackError}</p> : null}
              {track && track.total_points > track.points.length ? (
                <p className="muted small">Route simplified to {formatNumber(track.points.length)} of {formatNumber(track.total_points)} points.</p>
              ) : null}
              {showMap ? (
                <StopMap rows={rows} track={track} showRouteStops={showRouteStops} live={live} formatClock={formatClock} formatDuration={formatDuration} describeUpkeep={describeUpkeep} onShowVisits={showVisits} colorBy={mapColorBy} tagColors={tagColors} tagLabels={tagLabels} />
              ) : null}
            </div>

            <div className="bulkbar">
              <span className="num">{formatNumber(selected.size)} selected</span>
              <input
                className="input bulk-note"
                value={markNote}
                maxLength={300}
                onChange={(e) => setMarkNote(e.target.value)}
                placeholder="Note (optional), for example: confirmed by photo from the crew"
                aria-label="Note for the hand mark"
              />
              <button className="btn btn-primary" disabled={!selected.size || markBusy} onClick={markCleaned}>Mark cleaned by hand</button>
              <button className="btn" disabled={!selectedMarked.length || markBusy} onClick={removeMarks}>Remove hand mark</button>
              {selected.size ? <button className="btn" disabled={markBusy} onClick={() => setSelected(new Set())}>Clear selection</button> : null}
              {markMessage ? <span className="note">{markMessage}</span> : null}
            </div>

            {!rows.length ? <p className="muted">No stops match these filters.</p> : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="col-check"><input type="checkbox" checked={allOnPageSelected} onChange={togglePage} aria-label="Select all stops on this page" /></th>
                      <th>Stop</th><th>Tag</th><th>Status</th><th>Last cleaned</th><th>This day</th><th>Arrived</th><th className="num">Longest stay</th><th>Units</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((r) => {
                      const info = STATUS_INFO[r.status];
                      const isOpen = openStop === r.stop_id;
                      const rule = report.rules ? report.rules[r.classification] : null;
                      return [
                        <tr key={r.stop_id} id={`stop-row-${r.stop_id}`} className={isOpen ? "row-open" : ""}>
                          <td className="col-check">
                            <input type="checkbox" checked={selected.has(r.stop_id)} onChange={() => toggleSelected(r.stop_id)} aria-label={`Select ${r.name}`} />
                          </td>
                          <td>
                            <button className="link-btn" aria-expanded={isOpen} onClick={() => setOpenStop(isOpen ? null : r.stop_id)}>{r.name}</button>
                            {r.nearby_stop_ids.length ? <span className="muted small"> parked near {r.nearby_stop_ids.length} other stop{r.nearby_stop_ids.length === 1 ? "" : "s"}</span> : null}
                          </td>
                          <td><span className="tag-swatch" style={{ background: tagColors[r.classification] || FALLBACK_TAG_COLOR }} />{tagLabels[r.classification] || r.classification}</td>
                          <td>
                            <Badge tone={info.tone}>{info.label}</Badge>
                            {r.manual ? <span title={r.manual.note || "Marked cleaned by hand"}><Badge tone="plain">By hand</Badge></span> : null}
                            {r.cleaned_via ? <span title={`Cleaned together with ${r.cleaned_via.name}`}><Badge tone="plain">Linked</Badge></span> : null}
                            {r.status !== "cleaned" ? <div className="result-hint">{describeUpkeep(r)}</div> : null}
                          </td>
                          <td className="nowrap">{r.last_cleaned ? formatShortDate(r.last_cleaned) : <span className="muted">Never</span>}</td>
                          <td>
                            {r.gps_status === "not_visited" ? <span className="muted small">Not visited</span> : (
                              <Badge tone={VISIT_INFO[r.gps_status].tone}>{r.gps_status === "cleaned" ? "GPS cleaned" : "Too short"}</Badge>
                            )}
                            {r.open ? <span className="muted small"> still there</span> : null}
                            {r.gps_status === "short" && (r.min_dwell_minutes != null || rule) ? (
                              <div className="result-hint">Needs {r.min_dwell_minutes != null ? r.min_dwell_minutes : rule.min_dwell_minutes} min</div>
                            ) : null}
                          </td>
                          <td className="nowrap">{formatClock(r.first_arrival)}</td>
                          <td className="num">{formatDuration(r.longest_s)}</td>
                          <td>{r.units.join(", ")}</td>
                        </tr>,
                        isOpen ? (
                          <tr key={`${r.stop_id}-history`} className="row-detail">
                            <td colSpan={9}>
                              {r.manual ? (
                                <p className="manual-note">
                                  Marked cleaned by hand {formatTime(r.manual.marked_at)}
                                  {r.manual.note ? `: ${r.manual.note}` : "."}
                                </p>
                              ) : null}
                              {r.cleaned_via ? (
                                <p className="manual-note">Counted as cleaned because {r.cleaned_via.name}, which is cleaned together with it, was cleaned this day.</p>
                              ) : null}
                              <StopHistory appKey={appKey} stopId={r.stop_id} />
                            </td>
                          </tr>
                        ) : null,
                      ];
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {rows.length ? (
              <div className="pager">
                <label className="pager-size">
                  Show
                  <select className="input input-inline" value={pageSize} onChange={(e) => changePageSize(e.target.value)} aria-label="Stops per page">
                    {REPORT_PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                  per page
                </label>
                <span className="num">{formatNumber(page * pageSize + 1)}–{formatNumber(Math.min(rows.length, (page + 1) * pageSize))} of {formatNumber(rows.length)}</span>
                <button className="btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</button>
                <button className="btn" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
            ) : null}
          </>
        ) : null}
      </section>

      {report && report.units.length ? (
        <section className="panel">
          <h2 className="panel-title">Units this day</h2>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Unit</th><th className="num">Cleaned</th><th className="num">Too short</th><th className="num">Time at cleaned stops</th><th>First arrival</th><th>Last departure</th></tr>
              </thead>
              <tbody>
                {report.units.map((u) => (
                  <tr key={u.device_id}>
                    <td>{u.device_name}</td>
                    <td className="num">{formatNumber(u.cleaned)}</td>
                    <td className="num">{formatNumber(u.short)}</td>
                    <td className="num">{formatDuration(u.time_at_cleaned_s)}</td>
                    <td>{formatClock(u.first_start)}</td>
                    <td>{formatClock(u.last_end)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {report && report.rules ? (
        <p className="muted small">
          Cleaning rules: {rulesSummary(report.rules)}.{" "}
          <button className="link-btn" onClick={onOpenSettings}>Change in Settings</button>
        </p>
      ) : null}
    </div>
  );
}

const PAGE_SIZE = 100;

const MATCH_LABELS = {
  name: "by name",
  name_and_position: "by name and position (shared names)",
  position: "by position (renamed in Titan)",
};

function GroupImportPanel({ appKey, onAuthError, onApplied, onClose }) {
  const { labels: tagLabels, ids: tagIds } = useTags();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [tagMap, setTagMap] = useState({});
  const [onlyUnclassified, setOnlyUnclassified] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const fail = (e) => {
    if (e.status === 401) onAuthError(e.message);
    else setError(e.message);
  };

  const check = async () => {
    if (!file) return;
    setBusy(true);
    setError("");
    setDone(null);
    setPreview(null);
    try {
      const res = await apiUpload("/landmarks/group-import/preview", appKey, file);
      const map = {};
      res.groups.forEach((g) => { map[g.key] = g.suggested_tag || ""; });
      setTagMap(map);
      setPreview(res);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await apiFetch("/landmarks/group-import/apply", appKey, {
        method: "POST",
        body: JSON.stringify({ tag_map: tagMap, only_unclassified: onlyUnclassified }),
      });
      setDone(res);
      setPreview(null);
      setFile(null);
      onApplied();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const matchedBy = preview ? Object.keys(preview.matched_by || {}) : [];

  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2 className="panel-title">Import Titan groups</h2>
          <p className="muted small import-copy">
            Upload the landmark export from the Titan website (.xlsx or .csv). Each row is matched to a landmark StopCheck
            already has; no landmarks are added. You review the matches and choose a tag for each group before anything changes.
          </p>
        </div>
        {onClose ? <button className="btn btn-quiet" onClick={onClose}>Close</button> : null}
      </div>

      <div className="actions actions-tight">
        <input
          key={done ? done.applied_at : "file"}
          type="file"
          accept=".xlsx,.csv"
          aria-label="Titan landmark export"
          onChange={(e) => { setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null); setPreview(null); setError(""); }}
        />
        <button className="btn btn-primary" disabled={!file || busy} onClick={check}>{busy && !preview ? "Checking…" : "Check file"}</button>
      </div>

      {error ? <p className="error-box">{error}</p> : null}
      {done ? (
        <p className="manual-note import-done">
          Groups set on {formatNumber(done.landmarks)} landmarks ({formatNumber(done.groups_changed)} changed), {formatNumber(done.tags_changed)} tag{done.tags_changed === 1 ? "" : "s"} changed.
          {done.tags_changed ? " Visits for the last 7 days are being recalculated." : ""}
        </p>
      ) : null}

      {preview ? (
        <div>
          <dl className="facts">
            <div><dt>Rows in file</dt><dd>{formatNumber(preview.rows)}</dd></div>
            <div><dt>Matched to a landmark</dt><dd>{formatNumber(preview.matched)}</dd></div>
            <div><dt>Not matched</dt><dd>{formatNumber(preview.unmatched_total)}</dd></div>
            <div><dt>StopCheck landmarks not in file</dt><dd>{formatNumber(preview.not_in_file)}</dd></div>
          </dl>
          {matchedBy.length ? (
            <p className="muted small import-copy">
              Matched {matchedBy.map((k) => `${formatNumber(preview.matched_by[k])} ${MATCH_LABELS[k] || k}`).join(", ")}.
              {preview.duplicates ? ` ${formatNumber(preview.duplicates)} repeated row${preview.duplicates === 1 ? "" : "s"} skipped.` : ""}
              {preview.not_in_file ? " Landmarks not in the file keep their current group." : ""}
            </p>
          ) : null}

          <div className="table-wrap">
            <table className="table table-compact import-table">
              <thead>
                <tr>
                  <th>Titan group</th>
                  <th className="num">Landmarks</th>
                  <th className="num">Still unclassified</th>
                  <th>Tag to give them</th>
                </tr>
              </thead>
              <tbody>
                {preview.groups.map((g) => (
                  <tr key={g.key}>
                    <td className="rules-tag">{g.group || <span className="muted">No group</span>}</td>
                    <td className="num">{formatNumber(g.landmarks)}</td>
                    <td className="num">{formatNumber(g.unclassified)}</td>
                    <td>
                      <select
                        className="input input-inline"
                        value={tagMap[g.key] || ""}
                        onChange={(e) => setTagMap((m) => ({ ...m, [g.key]: e.target.value }))}
                        aria-label={`Tag for ${g.group || "no group"}`}
                      >
                        <option value="">Don't change tags</option>
                        {tagIds.map((c) => <option key={c} value={c}>{tagLabels[c]}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <label className="check-inline import-check">
            <input type="checkbox" checked={onlyUnclassified} onChange={(e) => setOnlyUnclassified(e.target.checked)} />
            Only tag landmarks that are still Unclassified (keep tags already set by hand)
          </label>

          {preview.unmatched_total ? (
            <details className="import-unmatched">
              <summary>{formatNumber(preview.unmatched_total)} row{preview.unmatched_total === 1 ? "" : "s"} not matched (these are skipped)</summary>
              <div className="table-wrap">
                <table className="table table-compact">
                  <thead><tr><th className="num">Row</th><th>Name</th><th>Group</th><th>Why</th></tr></thead>
                  <tbody>
                    {preview.unmatched.map((u) => (
                      <tr key={`${u.row}-${u.name}`}>
                        <td className="num">{u.row}</td>
                        <td>{u.name || <span className="muted">No name</span>}</td>
                        <td>{u.group || <span className="muted">None</span>}</td>
                        <td>{u.reason_label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.unmatched_total > preview.unmatched.length ? <p className="muted small">Showing the first {formatNumber(preview.unmatched.length)}.</p> : null}
            </details>
          ) : null}

          <div className="actions">
            <button className="btn btn-primary" disabled={busy || !preview.matched} onClick={apply}>
              {busy ? "Applying…" : `Apply to ${formatNumber(preview.matched)} landmarks`}
            </button>
            <button className="btn" disabled={busy} onClick={() => setPreview(null)}>Cancel</button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function TagsPanel({ appKey, onAuthError, onChanged }) {
  const { reload: reloadTags } = useTags();
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [newLabel, setNewLabel] = useState("");
  const [newCleaning, setNewCleaning] = useState(true);
  const [newColor, setNewColor] = useState("");
  const [confirmDelete, setConfirmDelete] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const accept = useCallback((res) => {
    setData({ tags: res.tags, counts: res.counts || {} });
    const next = {};
    res.tags.forEach((t) => { next[t.id] = { label: t.label, cleaning: t.cleaning, color: t.color }; });
    setDrafts(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiFetch("/tags", appKey)
      .then((res) => { if (!cancelled) accept(res); })
      .catch((e) => {
        if (cancelled) return;
        if (e.status === 401) onAuthError(e.message);
        else setError(e.message);
      });
    return () => { cancelled = true; };
  }, [appKey, accept, onAuthError]);

  const run = async (request, describe) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await request();
      accept(res);
      setConfirmDelete("");
      setMessage(describe(res));
      reloadTags();
      onChanged();
      return true;
    } catch (e) {
      if (e.status === 401) onAuthError(e.message);
      else setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const setDraft = (id, field, value) => {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], [field]: value } }));
  };

  const addTag = async () => {
    const label = newLabel.trim();
    const ok = await run(
      () => apiFetch("/tags", appKey, { method: "POST", body: JSON.stringify({ label, cleaning: newCleaning, color: newColor || null }) }),
      () => `Added ${label}.${newCleaning ? " Set its cleaning rule above." : ""}`,
    );
    if (ok) {
      setNewLabel("");
      setNewCleaning(true);
      setNewColor("");
    }
  };

  const saveTag = (id) => {
    const d = drafts[id];
    return run(
      () => apiFetch(`/tags/${encodeURIComponent(id)}`, appKey, { method: "PUT", body: JSON.stringify({ label: d.label, cleaning: d.cleaning, color: d.color }) }),
      (res) => `Saved ${d.label.trim()}.${res.recompute_started ? " The last 7 days are being recalculated." : ""}`,
    );
  };

  const deleteTag = (id) => run(
    () => apiFetch(`/tags/${encodeURIComponent(id)}`, appKey, { method: "DELETE" }),
    (res) => `Deleted.${res.reassigned ? ` ${formatNumber(res.reassigned)} landmark${res.reassigned === 1 ? "" : "s"} moved to ${res.tags[0].label}; the last 7 days are being recalculated.` : ""}`,
  );

  const unclassifiedLabel = data ? data.tags[0].label : "Unclassified";

  return (
    <section className="panel">
      <h2 className="panel-title">Tags</h2>
      <p className="muted import-copy">
        Tags sort landmarks into kinds of stop. Each cleaning tag gets its own cleaning rule above; landmarks with a tag that
        isn't a cleaning stop are left out of the report. The colour marks the tag on the maps. Deleting a tag moves its
        landmarks to {unclassifiedLabel}.
      </p>
      {error ? <p className="error-box">{error}</p> : null}
      {!data && !error ? <p className="muted">Loading tags…</p> : null}
      {data ? (
        <div className="table-wrap">
          <table className="table tags-table">
            <thead>
              <tr><th>Colour</th><th>Name</th><th>Cleaning stop</th><th className="num">Landmarks</th><th /></tr>
            </thead>
            <tbody>
              {data.tags.map((t) => {
                const d = drafts[t.id] || { label: t.label, cleaning: t.cleaning, color: t.color };
                const dirty = d.label !== t.label || d.cleaning !== t.cleaning || d.color !== t.color;
                const count = data.counts[t.id] || 0;
                return (
                  <tr key={t.id}>
                    <td>
                      <input
                        type="color"
                        className="tag-color"
                        value={d.color || FALLBACK_TAG_COLOR}
                        onChange={(e) => setDraft(t.id, "color", e.target.value)}
                        aria-label={`Colour for ${t.label}`}
                      />
                    </td>
                    <td>
                      <input
                        className="input tag-input"
                        value={d.label}
                        maxLength={40}
                        onChange={(e) => setDraft(t.id, "label", e.target.value)}
                        aria-label={`Name for ${t.label}`}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={d.cleaning}
                        disabled={t.builtin}
                        onChange={(e) => setDraft(t.id, "cleaning", e.target.checked)}
                        aria-label={`${t.label} is a cleaning stop`}
                        title={t.builtin ? "Fixed for this built-in tag" : undefined}
                      />
                    </td>
                    <td className="num">{formatNumber(count)}</td>
                    <td className="tag-actions">
                      <button className="btn" disabled={busy || !dirty || !d.label.trim()} onClick={() => saveTag(t.id)}>Save</button>
                      {t.builtin ? <span className="muted small">Built in</span> : confirmDelete === t.id ? (
                        <>
                          <button className="btn btn-danger" disabled={busy} onClick={() => deleteTag(t.id)}>
                            {count ? `Delete, move ${formatNumber(count)} to ${unclassifiedLabel}` : "Confirm delete"}
                          </button>
                          <button className="btn btn-quiet" disabled={busy} onClick={() => setConfirmDelete("")}>Keep</button>
                        </>
                      ) : (
                        <button className="btn btn-quiet" disabled={busy} onClick={() => setConfirmDelete(t.id)}>Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {data ? (
        <div className="actions tag-add">
          <input
            type="color"
            className="tag-color"
            value={newColor || "#2f7a4a"}
            onChange={(e) => setNewColor(e.target.value)}
            aria-label="Colour for the new tag"
          />
          <input
            className="input tag-input"
            value={newLabel}
            maxLength={40}
            placeholder="New tag name, for example Trash can"
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && newLabel.trim() && !busy) addTag(); }}
            aria-label="New tag name"
          />
          <label className="check-inline tag-check">
            <input type="checkbox" checked={newCleaning} onChange={(e) => setNewCleaning(e.target.checked)} />
            Cleaning stop
          </label>
          <button className="btn btn-primary" disabled={busy || !newLabel.trim()} onClick={addTag}>Add tag</button>
        </div>
      ) : null}
      {data && !newColor ? <p className="muted small tag-hint">Leave the colour as it is to get the next unused one.</p> : null}
      {message ? <p className="note">{message}</p> : null}
    </section>
  );
}

function LandmarkSourcePanel({ appKey, meta, onAuthError, onChanged }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const local = meta.source === "local";

  const goLocal = async () => {
    setBusy(true);
    setError("");
    try {
      await apiFetch("/landmarks/go-local", appKey, { method: "POST" });
      setConfirming(false);
      onChanged();
    } catch (e) {
      if (e.status === 401) onAuthError(e.message);
      else setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2 className="panel-title">Landmark source</h2>
          {local ? (
            <p className="muted import-copy">
              Landmarks have been managed in Stop Check since {formatTime(meta.local_since)}. They are no longer synced from
              Titan: add, edit, move and delete them on the Landmarks tab. GPS data still comes from Titan.
            </p>
          ) : (
            <p className="muted import-copy">
              Landmarks are still synced from Titan{meta.last_sync_at ? `, last on ${formatTime(meta.last_sync_at)}` : ""}.
              Once every landmark you need has been imported, stop syncing so landmarks can be added, edited, moved and
              deleted here. GPS data keeps coming from Titan either way.
            </p>
          )}
        </div>
        <Badge tone={local ? "ok" : "warn"}>{local ? "Managed in Stop Check" : "Synced from Titan"}</Badge>
      </div>
      {!local && !confirming ? (
        <div className="actions">
          <button className="btn" onClick={() => setConfirming(true)}>Stop syncing with Titan</button>
        </div>
      ) : null}
      {!local && confirming ? (
        <div className="confirm-box">
          <p className="confirm-text">
            Stop syncing landmarks from Titan? This can't be undone from the app. For one last update first, use “Pull GPS
            data and landmarks” on the Status tab and wait for it to finish.
          </p>
          <div className="actions">
            <button className="btn btn-primary" disabled={busy} onClick={goLocal}>{busy ? "Switching…" : "Yes, stop syncing"}</button>
            <button className="btn" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
          </div>
        </div>
      ) : null}
      {error ? <p className="error-box">{error}</p> : null}
    </section>
  );
}

const BACKUP_LABELS = {
  landmarks: "Landmarks",
  app_state: "Settings (tags, rules, landmark source)",
  visits: "Visits",
  manual_marks: "Hand marks",
  gps_points: "GPS points",
};

function formatBytes(n) {
  if (n == null) return "";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function BackupPanel({ appKey, onAuthError, onRestored }) {
  const { reload: reloadTags } = useTags();
  const [info, setInfo] = useState(null);
  const [withPoints, setWithPoints] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [file, setFile] = useState(null);
  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handle = useCallback((e) => {
    if (e.status === 401) onAuthError(e.message);
    else setError(e.message);
  }, [onAuthError]);

  useEffect(() => {
    apiFetch("/backup/info", appKey)
      .then((res) => { setInfo(res); if (res.restore && res.restore.running) setStatus(res.restore); })
      .catch(handle);
  }, [appKey, handle]);

  const running = Boolean(status && status.running);

  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => {
      apiFetch("/restore/status", appKey)
        .then((st) => {
          setStatus(st);
          if (!st.running) {
            if (st.error) setError(st.error);
            else {
              setMessage("Restore finished. StopCheck now has the backup's data.");
              onRestored();
              reloadTags();
            }
            apiFetch("/backup/info", appKey).then(setInfo).catch(() => {});
          }
        })
        .catch(() => { /* try again on the next tick */ });
    }, 2000);
    return () => clearInterval(id);
  }, [appKey, running, onRestored, reloadTags]);

  const download = async () => {
    setDownloading(true);
    setError("");
    setMessage("");
    try {
      const res = await fetch(`${API}/backup?include_points=${withPoints}`, { headers: { "X-App-Key": appKey } });
      if (res.status === 401) {
        onAuthError("Access key is missing or wrong.");
        return;
      }
      if (!res.ok) throw new Error(`Backup failed with status ${res.status}`);
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="([^"]+)"/);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "stopcheck-backup.json.gz";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setMessage(`Backup downloaded (${formatBytes(blob.size)}). Keep it somewhere safe, outside this computer too.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloading(false);
    }
  };

  const check = async () => {
    if (!file) return;
    setChecking(true);
    setError("");
    setMessage("");
    setPreview(null);
    setConfirmed(false);
    try {
      setPreview(await apiUpload("/restore/preview", appKey, file));
    } catch (e) {
      handle(e);
    } finally {
      setChecking(false);
    }
  };

  const apply = async () => {
    setError("");
    setMessage("");
    try {
      await apiFetch("/restore/apply", appKey, { method: "POST", body: JSON.stringify({ token: preview.token }) });
      setStatus({ running: true });
      setPreview(null);
      setFile(null);
      setConfirmed(false);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      handle(e);
    }
  };

  const counts = info ? info.counts : null;
  const progress = status && status.progress ? Object.values(status.progress).reduce((a, b) => a + b, 0) : 0;

  return (
    <section className="panel">
      <h2 className="panel-title">Backup and restore</h2>
      <p className="muted rules-copy">
        A backup holds landmarks (with their tags, groups, own cleaning rules and links), tags, cleaning rules, visits and
        hand marks. GPS points are optional: they make the file much larger, and Titan only keeps about a week of history
        to collect again.
      </p>
      {counts ? (
        <p className="muted small">
          Now: {formatNumber(counts.landmarks)} landmarks, {formatNumber(counts.visits)} visits, {formatNumber(counts.manual_marks)} hand
          marks, {formatNumber(counts.gps_points)} GPS points.
        </p>
      ) : null}
      <h3 className="backup-sub">Download a backup</h3>
      <label className="check-inline backup-check">
        <input type="checkbox" checked={withPoints} onChange={(e) => setWithPoints(e.target.checked)} />
        Include GPS points{counts ? ` (${formatNumber(counts.gps_points)})` : ""}
      </label>
      <div className="actions">
        <button className="btn btn-primary" disabled={downloading || running} onClick={download}>{downloading ? "Preparing backup…" : "Download backup"}</button>
      </div>

      <h3 className="backup-sub">Restore a backup</h3>
      <p className="muted small">
        Choose a backup file and check it first. Restoring replaces the data listed in the backup; GPS points are only
        replaced when the backup includes them. Download a backup of the current data first if you might want it back.
      </p>
      <div className="actions actions-tight">
        <input
          ref={fileRef}
          className="input input-inline backup-file"
          type="file"
          accept=".gz,application/gzip"
          disabled={running}
          onChange={(e) => { setFile(e.target.files[0] || null); setPreview(null); setConfirmed(false); }}
          aria-label="Backup file"
        />
        <button className="btn" disabled={!file || checking || running} onClick={check}>{checking ? "Checking…" : "Check file"}</button>
      </div>

      {preview ? (
        <div className="confirm-box">
          <p className="confirm-text">
            <strong>{preview.filename}</strong> is a complete backup made {formatTime(preview.created_at)} ({formatBytes(preview.bytes)}).
            Restoring it replaces:
          </p>
          <ul className="backup-list">
            {preview.collections.map((c) => <li key={c}>{BACKUP_LABELS[c] || c}: {formatNumber(preview.counts[c])}</li>)}
          </ul>
          {!preview.includes_points ? <p className="muted small">It has no GPS points, so the current GPS history stays as it is.</p> : null}
          <label className="check-inline backup-check">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
            I understand the current data listed above will be replaced.
          </label>
          <div className="actions">
            <button className="btn btn-danger" disabled={!confirmed || running} onClick={apply}>Restore this backup</button>
            <button className="btn" onClick={() => { setPreview(null); setConfirmed(false); }}>Cancel</button>
          </div>
        </div>
      ) : null}

      {running ? (
        <p className="note">Restoring… {progress ? `${formatNumber(progress)} records loaded.` : ""} Collection from Titan waits until it's done.</p>
      ) : null}
      {message ? <p className="manual-note backup-msg">{message}</p> : null}
      {error ? <p className="error-box">{error}</p> : null}
    </section>
  );
}

function SettingsView({ appKey, onAuthError }) {
  const [rules, setRules] = useState(null);
  const [ruleTags, setRuleTags] = useState([]);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  const handleError = useCallback((e) => {
    if (e.status === 401) onAuthError(e.message);
    else setError(e.message);
  }, [onAuthError]);

  useEffect(() => {
    let cancelled = false;
    apiFetch("/rules", appKey)
      .then((res) => {
        if (cancelled) return;
        setRuleTags(res.tags);
        setRules(res.by_tag);
      })
      .catch((e) => { if (!cancelled) handleError(e); });
    apiFetch("/landmarks/meta", appKey)
      .then((res) => { if (!cancelled) setMeta(res); })
      .catch((e) => { if (!cancelled) handleError(e); });
    return () => { cancelled = true; };
  }, [appKey, reloadToken, handleError]);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);
  const lastImport = meta ? meta.group_import : null;

  return (
    <div className="stack">
      <div>
        <h1 className="page-title">Settings</h1>
        {error ? <p className="error-box">{error}</p> : null}
      </div>
      {rules ? <RulesPanel appKey={appKey} rules={rules} ruleTags={ruleTags} onSaved={reload} /> : (!error ? <p className="muted">Loading rules…</p> : null)}
      <TagsPanel appKey={appKey} onAuthError={onAuthError} onChanged={reload} />
      {meta ? <LandmarkSourcePanel appKey={appKey} meta={meta} onAuthError={onAuthError} onChanged={reload} /> : null}
      <div>
        <GroupImportPanel appKey={appKey} onAuthError={onAuthError} onApplied={reload} />
        {lastImport ? (
          <p className="muted small settings-foot">
            Titan groups last imported {formatTime(lastImport.applied_at)}{lastImport.filename ? ` from ${lastImport.filename}` : ""}.
          </p>
        ) : null}
      </div>
      <BackupPanel appKey={appKey} onAuthError={onAuthError} onRestored={reload} />
    </div>
  );
}

const RULE_FIELDS = ["radius_m", "min_dwell_minutes", "stays_clean_days"];
const EMPTY_RULE_FORM = { radius_m: "", min_dwell_minutes: "", stays_clean_days: "" };
const EMPTY_LANDMARK_FORM = { name: "", coords: "", classification: "unclassified", group: "", notes: "", ...EMPTY_RULE_FORM };

// A landmark's own rule values as form text; blank means "use the tag's".
function overrideToForm(override) {
  const out = { ...EMPTY_RULE_FORM };
  RULE_FIELDS.forEach((k) => {
    if (override && override[k] != null) out[k] = String(override[k]);
  });
  return out;
}

function ruleFormBody(form) {
  const body = {};
  RULE_FIELDS.forEach((k) => {
    const v = String(form[k] == null ? "" : form[k]).trim();
    body[k] = v === "" ? null : Number(v);
  });
  return body;
}

function ruleFormKey(form) {
  return RULE_FIELDS.map((k) => String(form[k] == null ? "" : form[k]).trim()).join("|");
}

function hasOverride(override) {
  return Boolean(override && RULE_FIELDS.some((k) => override[k] != null));
}

// The distance drawn on the map: the landmark's own if it is a valid number, else the tag's.
function effectiveRadius(form, tagRule) {
  if (!tagRule) return null;
  const own = Number(String(form.radius_m || "").trim());
  if (String(form.radius_m || "").trim() !== "" && Number.isFinite(own) && own >= RADIUS_MIN_M && own <= RADIUS_MAX_M) return own;
  return tagRule.radius_m;
}

function LandmarkRuleFields({ form, onChange, tagRule, tagLabel, busy, standalone, onSaveRules, rulesChanged }) {
  if (!tagRule) {
    return (
      <div className="lm-rule">
        <h4 className="lm-rule-title">Cleaning rule</h4>
        <p className="muted small lm-editor-hint">{tagLabel} isn't a cleaning stop, so no cleaning rule applies.</p>
      </div>
    );
  }
  const set = (field) => (e) => onChange(field, e.target.value);
  const custom = RULE_FIELDS.some((k) => String(form[k] || "").trim() !== "");
  return (
    <div className="lm-rule">
      <h4 className="lm-rule-title">Cleaning rule for this landmark</h4>
      <p className="muted small lm-editor-hint">
        Leave a value blank to use the {tagLabel} default. A value set here is used instead of the tag's. Drag the handle on the circle to change the distance.
      </p>
      <div className="lm-rule-grid">
        <label className="field-label" htmlFor="lm-f-radius">Distance (m)</label>
        <input id="lm-f-radius" className="input" type="number" min={RADIUS_MIN_M} max={RADIUS_MAX_M} step="1" value={form.radius_m}
          placeholder={`${tagRule.radius_m} (default)`} onChange={set("radius_m")} />
        <label className="field-label" htmlFor="lm-f-minutes">Minimum time (min)</label>
        <input id="lm-f-minutes" className="input" type="number" min="1" max="60" step="0.5" value={form.min_dwell_minutes}
          placeholder={`${tagRule.min_dwell_minutes} (default)`} onChange={set("min_dwell_minutes")} />
        <label className="field-label" htmlFor="lm-f-days">Stays clean (days)</label>
        <input id="lm-f-days" className="input" type="number" min="0" max="365" step="1" value={form.stays_clean_days}
          placeholder={`${tagRule.stays_clean_days} (default)`} onChange={set("stays_clean_days")} />
      </div>
      <div className="actions actions-tight">
        {custom ? (
          <button className="btn btn-quiet" disabled={busy} onClick={() => RULE_FIELDS.forEach((k) => onChange(k, ""))}>Use tag defaults</button>
        ) : null}
        {standalone ? (
          <button className="btn btn-primary" disabled={busy || !rulesChanged} onClick={onSaveRules}>{busy ? "Saving…" : "Save cleaning rule"}</button>
        ) : null}
      </div>
    </div>
  );
}

const LINK_NEAR_M = 150;
const LINK_CHOICES = 12;

function distanceM(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371008.8 * Math.asin(Math.min(1, Math.sqrt(h)));
}

// Landmarks cleaned together with this one, and nearby ones that can be added.
function LandmarkLinks({ linked, choices, busy, onLink, onUnlink }) {
  const [pick, setPick] = useState("");
  useEffect(() => {
    if (pick && !choices.some((c) => c.id === pick)) setPick("");
  }, [choices, pick]);
  return (
    <div className="lm-rule">
      <h4 className="lm-rule-title">Cleaned together with</h4>
      <p className="muted small lm-editor-hint">
        When any of these is cleaned, by GPS or by hand, they all count as cleaned that day.
      </p>
      {linked.length ? (
        <ul className="lm-links">
          {linked.map((l) => (
            <li key={l.id}>
              <span>{l.name || "No name"}</span>
              <button className="btn btn-quiet btn-small" disabled={busy} onClick={() => onUnlink(l.id)}>Remove</button>
            </li>
          ))}
        </ul>
      ) : <p className="muted small lm-editor-hint">Not linked to any other landmark.</p>}
      <div className="actions actions-tight">
        <select className="input input-inline lm-link-pick" value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Nearby landmark to link" disabled={busy || !choices.length}>
          <option value="">{choices.length ? "Add a nearby landmark…" : `No other landmarks within ${LINK_NEAR_M} m`}</option>
          {choices.map((c) => <option key={c.id} value={c.id}>{c.name || "No name"} ({Math.round(c.distance)} m)</option>)}
        </select>
        <button className="btn" disabled={busy || !pick} onClick={() => onLink(pick)}>Link</button>
      </div>
    </div>
  );
}

function LandmarkEditor({ mode, local, form, onChange, tags, groups, busy, error, confirmDelete, onSave, onCancel, onAskDelete, onDelete, onOpenSettings, tagRule, onSaveRules, rulesChanged, linkPanel }) {
  const set = (field) => (e) => onChange(field, e.target.value);
  const readOnly = !local;
  const tagLabel = (tags.find((t) => t.id === form.classification) || {}).label || form.classification;
  const title = mode === "new" ? "New landmark" : readOnly ? "Landmark" : "Edit landmark";
  return (
    <div className="lm-editor">
      <div className="lm-editor-head">
        <h3 className="lm-editor-title">{title}</h3>
        <button className="btn btn-quiet" onClick={onCancel}>Close</button>
      </div>
      {mode === "new" ? (
        <p className="muted small lm-editor-hint">Click the map to drop the pin, then drag it to adjust. Or type or paste coordinates.</p>
      ) : local ? (
        <p className="muted small lm-editor-hint">Drag the pin to move this landmark.</p>
      ) : null}
      <label className="field-label" htmlFor="lm-f-name">Name</label>
      <input id="lm-f-name" className="input" value={form.name} maxLength={120} onChange={set("name")} disabled={readOnly} />
      <label className="field-label" htmlFor="lm-f-coords">Coordinates (latitude, longitude)</label>
      <input id="lm-f-coords" className="input" value={form.coords} onChange={set("coords")} placeholder="52.268100, -113.811200" disabled={readOnly} />
      {form.coords && !parseCoords(form.coords) ? <p className="error-text small">Use latitude, longitude, for example 52.268100, -113.811200.</p> : null}
      <label className="field-label" htmlFor="lm-f-tag">Tag</label>
      <select id="lm-f-tag" className="input" value={form.classification} onChange={set("classification")} disabled={readOnly}>
        {tags.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
      </select>
      <label className="field-label" htmlFor="lm-f-group">Group</label>
      <input id="lm-f-group" className="input" value={form.group} maxLength={80} onChange={set("group")} list="lm-groups" disabled={readOnly} />
      <datalist id="lm-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
      <label className="field-label" htmlFor="lm-f-notes">Notes</label>
      <textarea id="lm-f-notes" className="input lm-notes" value={form.notes} maxLength={500} rows={3} onChange={set("notes")} disabled={readOnly} />
      {mode === "edit" || local ? (
        <LandmarkRuleFields
          form={form}
          onChange={onChange}
          tagRule={tagRule}
          tagLabel={tagLabel}
          busy={busy}
          standalone={readOnly}
          onSaveRules={onSaveRules}
          rulesChanged={rulesChanged}
        />
      ) : null}
      {mode === "edit" ? linkPanel : null}
      {error ? <p className="error-box">{error}</p> : null}
      {readOnly ? (
        <p className="muted small lm-editor-hint">
          Landmarks are still synced from Titan, so only their tag can be changed (in the list below).{" "}
          <button className="link-btn" onClick={onOpenSettings}>Switch to local landmarks in Settings</button>
        </p>
      ) : (
        <div className="actions">
          <button className="btn btn-primary" disabled={busy} onClick={onSave}>{busy ? "Saving…" : mode === "new" ? "Add landmark" : "Save"}</button>
          <button className="btn" disabled={busy} onClick={onCancel}>Cancel</button>
          {mode === "edit" && !confirmDelete ? <button className="btn btn-quiet lm-delete" disabled={busy} onClick={onAskDelete}>Delete</button> : null}
        </div>
      )}
      {!readOnly && mode === "edit" && confirmDelete ? (
        <div className="confirm-box">
          <p className="confirm-text">Delete this landmark? Its visits and hand marks are deleted too.</p>
          <div className="actions">
            <button className="btn btn-danger" disabled={busy} onClick={onDelete}>Yes, delete</button>
            <button className="btn" disabled={busy} onClick={() => onAskDelete(false)}>Keep it</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function LandmarksView({ appKey, onAuthError, onOpenSettings }) {
  const { list: tagList, labels: tagLabels, colors: tagColors, ids: tagIds } = useTags();
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({ q: "", group: "", classification: "", flag: "", include_removed: false });
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkClass, setBulkClass] = useState("bus_stop");
  const [message, setMessage] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [mapData, setMapData] = useState({ items: [], truncated: false });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_LANDMARK_FORM);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [focusToken, setFocusToken] = useState(0);
  const [mapMessage, setMapMessage] = useState("");
  const [ruleSet, setRuleSet] = useState({});
  const [linked, setLinked] = useState([]);
  const [linkBusy, setLinkBusy] = useState(false);
  const mapBlockRef = useRef(null);

  const handleError = useCallback((e) => {
    if (e.status === 401) onAuthError(e.message);
    else setError(e.message);
  }, [onAuthError]);

  useEffect(() => {
    apiFetch("/landmarks/meta", appKey).then(setMeta).catch(handleError);
  }, [appKey, handleError, reloadToken]);

  useEffect(() => {
    apiFetch("/rules", appKey).then((res) => setRuleSet(res.by_tag || {})).catch(handleError);
  }, [appKey, handleError, reloadToken]);

  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((f) => (f.q === searchText ? f : { ...f, q: searchText }));
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [searchText]);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({
      q: filters.q,
      group: filters.group,
      classification: filters.classification,
      flag: filters.flag,
      include_removed: String(filters.include_removed),
      skip: String(page * PAGE_SIZE),
      limit: String(PAGE_SIZE),
    });
    apiFetch(`/landmarks?${params.toString()}`, appKey)
      .then((res) => { if (!cancelled) { setResult(res); setError(""); } })
      .catch((e) => { if (!cancelled) handleError(e); });
    return () => { cancelled = true; };
  }, [appKey, filters, page, reloadToken, handleError]);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({
      q: filters.q,
      group: filters.group,
      classification: filters.classification,
      flag: filters.flag,
      include_removed: String(filters.include_removed),
    });
    apiFetch(`/landmarks/map?${params.toString()}`, appKey)
      .then((res) => { if (!cancelled) setMapData(res); })
      .catch((e) => { if (!cancelled) handleError(e); });
    return () => { cancelled = true; };
  }, [appKey, filters, reloadToken, handleError]);

  const setFilter = (name, value) => {
    setFilters((f) => ({ ...f, [name]: value }));
    setPage(0);
  };

  const items = useMemo(() => (result ? result.items : []), [result]);
  const pageIds = items.map((i) => i.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const local = Boolean(meta && meta.source === "local");

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const applyClassification = async () => {
    setMessage("");
    try {
      const res = await apiFetch("/landmarks/classify", appKey, {
        method: "POST",
        body: JSON.stringify({ ids: Array.from(selected), classification: bulkClass }),
      });
      setMessage(`Marked ${res.matched} landmark${res.matched === 1 ? "" : "s"} as ${(tagLabels[bulkClass] || bulkClass).toLowerCase()}.`);
      setSelected(new Set());
      setReloadToken((t) => t + 1);
    } catch (e) {
      handleError(e);
    }
  };

  const colorFor = useCallback((p) => tagColors[p.classification] || FALLBACK_TAG_COLOR, [tagColors]);

  const openLandmark = useCallback(async (id, scroll) => {
    setEditError("");
    setConfirmDelete(false);
    setMapMessage("");
    try {
      const res = await apiFetch(`/landmarks/${encodeURIComponent(id)}`, appKey);
      const lm = res.landmark;
      const ruleForm = overrideToForm(lm.rule_override);
      setEditing({ mode: "edit", id: lm.id, savedRules: ruleFormKey(ruleForm) });
      setLinked(res.linked || []);
      setForm({
        name: lm.name || "",
        coords: lm.lat != null && lm.lng != null ? formatCoords(lm.lat, lm.lng) : "",
        classification: lm.classification || "unclassified",
        group: lm.group || "",
        notes: lm.notes || "",
        ...ruleForm,
      });
      setFocusToken((t) => t + 1);
      if (scroll && mapBlockRef.current) mapBlockRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (e) {
      if (e.status === 401) onAuthError(e.message);
      else setMapMessage(e.message);
    }
  }, [appKey, onAuthError]);

  const startNew = () => {
    setEditError("");
    setConfirmDelete(false);
    setMapMessage("");
    setEditing({ mode: "new", savedRules: ruleFormKey(EMPTY_RULE_FORM) });
    setForm({ ...EMPTY_LANDMARK_FORM });
  };

  const closeEditor = () => {
    setEditing(null);
    setEditError("");
    setConfirmDelete(false);
  };

  const changeField = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const placePin = useCallback((lat, lng) => setForm((f) => ({ ...f, coords: formatCoords(lat, lng) })), []);
  const setRadiusFromMap = useCallback((metres) => setForm((f) => ({ ...f, radius_m: String(metres) })), []);
  const rulesChanged = Boolean(editing && ruleFormKey(form) !== editing.savedRules);
  const tagRule = ruleSet[form.classification] || null;

  const putRules = (id) => apiFetch(`/landmarks/${encodeURIComponent(id)}/rules`, appKey, {
    method: "PUT",
    body: JSON.stringify(ruleFormBody(form)),
  });

  const editingId = editing && editing.mode === "edit" ? editing.id : null;
  const linkChoices = useMemo(() => {
    if (!editingId) return [];
    const here = mapData.items.find((p) => p.id === editingId);
    if (!here) return [];
    const taken = new Set(linked.map((l) => l.id));
    return mapData.items
      .filter((p) => p.id !== editingId && !taken.has(p.id))
      .map((p) => ({ id: p.id, name: p.name, distance: distanceM(here, p) }))
      .filter((p) => p.distance <= LINK_NEAR_M)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, LINK_CHOICES);
  }, [editingId, mapData, linked]);

  const refreshLinked = async (id) => {
    const res = await apiFetch(`/landmarks/${encodeURIComponent(id)}`, appKey);
    setLinked(res.linked || []);
  };

  const linkWith = async (otherId) => {
    setLinkBusy(true);
    setEditError("");
    try {
      await apiFetch("/landmarks/link", appKey, { method: "POST", body: JSON.stringify({ ids: [editingId, otherId] }) });
      await refreshLinked(editingId);
      setReloadToken((t) => t + 1);
    } catch (e) {
      if (e.status === 401) onAuthError(e.message);
      else setEditError(e.message);
    } finally {
      setLinkBusy(false);
    }
  };

  const unlinkOther = async (otherId) => {
    setLinkBusy(true);
    setEditError("");
    try {
      await apiFetch(`/landmarks/${encodeURIComponent(otherId)}/unlink`, appKey, { method: "POST" });
      await refreshLinked(editingId);
      setReloadToken((t) => t + 1);
    } catch (e) {
      if (e.status === 401) onAuthError(e.message);
      else setEditError(e.message);
    } finally {
      setLinkBusy(false);
    }
  };

  const linkSelected = async () => {
    setMessage("");
    try {
      const res = await apiFetch("/landmarks/link", appKey, { method: "POST", body: JSON.stringify({ ids: Array.from(selected) }) });
      setMessage(`Linked ${res.members} landmarks to be cleaned together: ${res.names.join(", ")}.`);
      setSelected(new Set());
      if (editingId) await refreshLinked(editingId);
      setReloadToken((t) => t + 1);
    } catch (e) {
      handleError(e);
    }
  };

  // Landmarks synced from Titan: only the cleaning rule can be saved here.
  const saveRulesOnly = async () => {
    setEditBusy(true);
    setEditError("");
    try {
      const res = await putRules(editing.id);
      setMapMessage(`Saved the cleaning rule for ${res.landmark.name}.${res.recompute_started ? " Visits for the last 7 days are being recalculated." : ""}`);
      setEditing(null);
      setReloadToken((t) => t + 1);
    } catch (e) {
      if (e.status === 401) onAuthError(e.message);
      else setEditError(e.message);
    } finally {
      setEditBusy(false);
    }
  };

  const saveLandmark = async () => {
    const pos = parseCoords(form.coords);
    if (!form.name.trim()) {
      setEditError("Give the landmark a name.");
      return;
    }
    if (!pos) {
      setEditError("Add coordinates: click the map, or type latitude, longitude.");
      return;
    }
    setEditBusy(true);
    setEditError("");
    const body = JSON.stringify({ name: form.name, lat: pos.lat, lng: pos.lng, classification: form.classification, group: form.group, notes: form.notes });
    try {
      const isNew = editing.mode === "new";
      const res = isNew
        ? await apiFetch("/landmarks", appKey, { method: "POST", body })
        : await apiFetch(`/landmarks/${encodeURIComponent(editing.id)}`, appKey, { method: "PUT", body });
      let recompute = res.recompute_started;
      if (rulesChanged) {
        try {
          const ruleRes = await putRules(res.landmark.id);
          recompute = recompute || ruleRes.recompute_started;
        } catch (ruleErr) {
          // The landmark is saved; keep the editor open on it so the rule can be fixed.
          if (ruleErr.status === 401) {
            onAuthError(ruleErr.message);
            return;
          }
          setEditing({ mode: "edit", id: res.landmark.id, savedRules: isNew ? ruleFormKey(EMPTY_RULE_FORM) : editing.savedRules });
          setEditError(`${isNew ? "Added" : "Saved"} the landmark, but not its cleaning rule: ${ruleErr.message}`);
          setReloadToken((t) => t + 1);
          return;
        }
      }
      setMapMessage(`${isNew ? "Added" : "Saved"} ${res.landmark.name}.${recompute ? " Visits for the last 7 days are being recalculated." : ""}`);
      setEditing(null);
      setReloadToken((t) => t + 1);
    } catch (e) {
      if (e.status === 401) onAuthError(e.message);
      else setEditError(e.message);
    } finally {
      setEditBusy(false);
    }
  };

  const deleteLandmark = async () => {
    setEditBusy(true);
    setEditError("");
    try {
      await apiFetch(`/landmarks/${encodeURIComponent(editing.id)}`, appKey, { method: "DELETE" });
      setMapMessage(`Deleted ${form.name || "the landmark"}.`);
      setEditing(null);
      setConfirmDelete(false);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(editing.id);
        return next;
      });
      setReloadToken((t) => t + 1);
    } catch (e) {
      if (e.status === 401) onAuthError(e.message);
      else setEditError(e.message);
    } finally {
      setEditBusy(false);
    }
  };

  const flagLabels = (meta && meta.flags) || {};
  const total = result ? result.total : 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const pin = editing ? parseCoords(form.coords) : null;
  const placing = Boolean(editing && editing.mode === "new" && local);
  const selectedId = editing && editing.mode === "edit" ? editing.id : null;

  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2 className="panel-title">Landmarks</h2>
          <p className="muted">
            {local
              ? "Add, edit, move and tag landmarks. Click a dot on the map or a name in the list to open it."
              : "Tag which Titan landmarks are cleaning stops. Syncing from Titan never changes a tag or group set here."}
          </p>
          <p className="muted small">
            {meta && meta.group_import
              ? `Titan groups last imported ${formatTime(meta.group_import.applied_at)}${meta.group_import.filename ? ` from ${meta.group_import.filename}` : ""}. `
              : "Titan groups haven't been imported yet. "}
            <button className="link-btn" onClick={onOpenSettings}>Import Titan groups in Settings</button>
          </p>
        </div>
        <button className="btn btn-primary" disabled={!local} onClick={startNew} title={local ? undefined : "Switch to local landmarks in Settings first"}>Add landmark</button>
      </div>

      {meta && !local ? (
        <p className="manual-note lm-local-note">
          Landmarks are still synced from Titan, so they can be tagged here but not added, moved or deleted.{" "}
          <button className="link-btn" onClick={onOpenSettings}>Switch to local landmarks in Settings</button>
        </p>
      ) : null}

      <div className="filters">
        <div className="filter">
          <label className="field-label" htmlFor="lm-q">Name</label>
          <input id="lm-q" className="input" value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Search names" />
        </div>
        {meta && meta.has_groups ? (
          <div className="filter">
            <label className="field-label" htmlFor="lm-group">Group</label>
            <select id="lm-group" className="input" value={filters.group} onChange={(e) => setFilter("group", e.target.value)}>
              <option value="">All groups</option>
              <option value="__none__">No group</option>
              {meta.groups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
        ) : null}
        <div className="filter">
          <label className="field-label" htmlFor="lm-class">Tag</label>
          <select id="lm-class" className="input" value={filters.classification} onChange={(e) => setFilter("classification", e.target.value)}>
            <option value="">All tags</option>
            {tagIds.map((c) => <option key={c} value={c}>{tagLabels[c]}</option>)}
          </select>
        </div>
        <div className="filter">
          <label className="field-label" htmlFor="lm-flag">Problems</label>
          <select id="lm-flag" className="input" value={filters.flag} onChange={(e) => setFilter("flag", e.target.value)}>
            <option value="">Any</option>
            <option value="__any__">Has a problem</option>
            {Object.keys(flagLabels).map((f) => <option key={f} value={f}>{flagLabels[f]}</option>)}
          </select>
        </div>
        <label className="check-inline">
          <input type="checkbox" checked={filters.include_removed} onChange={(e) => setFilter("include_removed", e.target.checked)} />
          Show removed from Titan
        </label>
      </div>

      <div className="map-block" ref={mapBlockRef}>
        <ul className="legend lm-legend" aria-label="Map legend">
          {tagList.map((t) => <li key={t.id}><span className="dot" style={{ background: tagColors[t.id] }} />{t.label}</li>)}
          {mapData.items.some((p) => p.link_id) ? <li><span className="line line-dashed" />Cleaned together</li> : null}
        </ul>
        <div className={`lm-edit-grid ${editing ? "lm-edit-open" : ""}`}>
          <LandmarkMap
            points={mapData.items}
            colorFor={colorFor}
            selectedId={selectedId}
            pin={pin}
            pinColor={tagColors[form.classification] || FALLBACK_TAG_COLOR}
            pinDraggable={local}
            pinRadius={editing ? effectiveRadius(form, tagRule) : null}
            radiusEditable={Boolean(editing && tagRule && (local || editing.mode === "edit"))}
            onRadiusChange={setRadiusFromMap}
            placing={placing}
            focusToken={focusToken}
            onSelect={(id) => openLandmark(id, false)}
            onPlace={placePin}
            onPinMove={placePin}
          />
          {editing ? (
            <LandmarkEditor
              mode={editing.mode}
              local={local}
              form={form}
              onChange={changeField}
              tags={tagList}
              groups={(meta && meta.groups) || []}
              busy={editBusy}
              error={editError}
              confirmDelete={confirmDelete}
              onSave={saveLandmark}
              onCancel={closeEditor}
              onAskDelete={(value) => setConfirmDelete(value !== false)}
              onDelete={deleteLandmark}
              onOpenSettings={onOpenSettings}
              tagRule={tagRule}
              onSaveRules={saveRulesOnly}
              rulesChanged={rulesChanged}
              linkPanel={(
                <LandmarkLinks linked={linked} choices={linkChoices} busy={linkBusy || editBusy} onLink={linkWith} onUnlink={unlinkOther} />
              )}
            />
          ) : null}
        </div>
        <p className="muted small lm-map-foot">
          {formatNumber(mapData.items.length)} landmark{mapData.items.length === 1 ? "" : "s"} on the map
          {mapData.truncated ? " (the first ones only; narrow the filters to see the rest)" : ""}.
        </p>
        {mapMessage ? <p className="manual-note">{mapMessage}</p> : null}
      </div>

      <div className="bulkbar">
        <span className="num">{formatNumber(selected.size)} selected</span>
        <select className="input input-inline" value={bulkClass} onChange={(e) => setBulkClass(e.target.value)} aria-label="Tag to apply">
          {tagIds.map((c) => <option key={c} value={c}>{tagLabels[c]}</option>)}
        </select>
        <button className="btn btn-primary" disabled={!selected.size || !tagIds.includes(bulkClass)} onClick={applyClassification}>Apply tag</button>
        <button className="btn" disabled={selected.size < 2} onClick={linkSelected} title="When any of them is cleaned, they all count as cleaned">Link as cleaned together</button>
        {selected.size ? <button className="btn" onClick={() => setSelected(new Set())}>Clear selection</button> : null}
        {message ? <span className="note">{message}</span> : null}
      </div>

      {error ? <p className="error-box">{error}</p> : null}
      {!result && !error ? <p className="muted">Loading landmarks…</p> : null}
      {result && !items.length ? (
        <p className="muted">{local ? "No landmarks match these filters." : "No landmarks match these filters. If this is the first run, pull landmarks from the Status tab."}</p>
      ) : null}

      {items.length ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="col-check"><input type="checkbox" checked={allOnPageSelected} onChange={togglePage} aria-label="Select all on this page" /></th>
                <th>Name</th>
                {meta && meta.has_groups ? <th>Group</th> : null}
                <th>Shape</th>
                <th className="num">Radius ft</th>
                <th>Tag</th>
                <th>Problems</th>
              </tr>
            </thead>
            <tbody>
              {items.map((lm) => (
                <tr key={lm.id} className={`${lm.removed_from_titan ? "row-removed" : ""} ${selectedId === lm.id ? "row-open" : ""}`}>
                  <td className="col-check"><input type="checkbox" checked={selected.has(lm.id)} onChange={() => toggle(lm.id)} aria-label={`Select ${lm.name}`} /></td>
                  <td>
                    <button className="link-btn" onClick={() => openLandmark(lm.id, true)}>{lm.name || "No name"}</button>
                    {lm.removed_from_titan ? <span className="muted small"> (removed from Titan)</span> : null}
                    {lm.source === "local" ? <span className="muted small"> (added here)</span> : null}
                    {hasOverride(lm.rule_override) ? <span className="muted small"> (own cleaning rule)</span> : null}
                    {lm.link_id ? <span className="muted small"> (cleaned together with others)</span> : null}
                  </td>
                  {meta && meta.has_groups ? <td>{lm.group || <span className="muted">None</span>}</td> : null}
                  <td>{lm.shape_type}</td>
                  <td className="num">{lm.radius_ft != null ? formatNumber(Math.round(lm.radius_ft)) : ""}</td>
                  <td>
                    <span className="tag-swatch" style={{ background: tagColors[lm.classification] || FALLBACK_TAG_COLOR }} />
                    {tagLabels[lm.classification] || lm.classification}
                  </td>
                  <td>{(lm.flags || []).map((f) => <Badge key={f} tone="warn">{flagLabels[f] || f}</Badge>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {total > PAGE_SIZE ? (
        <div className="pager">
          <button className="btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span className="num">{formatNumber(page * PAGE_SIZE + 1)}–{formatNumber(Math.min(total, (page + 1) * PAGE_SIZE))} of {formatNumber(total)}</span>
          <button className="btn" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      ) : null}
    </section>
  );
}

const TABS = [
  { id: "report", label: "Report" },
  { id: "status", label: "Status" },
  { id: "units", label: "Units" },
  { id: "landmarks", label: "Landmarks" },
  { id: "settings", label: "Settings", icon: true },
];

function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export default function App() {
  const [appKey, setAppKey] = useState(() => {
    try {
      return window.localStorage.getItem(KEY_STORAGE) || "";
    } catch (e) {
      return "";
    }
  });
  const [gateError, setGateError] = useState("");
  const [tab, setTab] = useState("report");
  const [reportDate, setReportDate] = useState(localToday);
  const [tagList, setTagList] = useState(DEFAULT_TAGS);

  const reloadTags = useCallback(() => {
    if (!appKey) return;
    apiFetch("/tags", appKey)
      .then((res) => setTagList(res.tags))
      .catch(() => {});
  }, [appKey]);

  useEffect(() => {
    reloadTags();
  }, [reloadTags]);

  const tagsValue = useMemo(() => tagsInfo(tagList, reloadTags), [tagList, reloadTags]);

  const openSettings = useCallback(() => setTab("settings"), []);

  const showReport = useCallback((date) => {
    setReportDate(date);
    setTab("report");
  }, []);

  const saveKey = (value) => {
    try {
      window.localStorage.setItem(KEY_STORAGE, value);
    } catch (e) {
      // storage unavailable: keep the key for this session only
    }
    setGateError("");
    setAppKey(value);
  };

  const handleAuthError = useCallback((msg) => {
    try {
      window.localStorage.removeItem(KEY_STORAGE);
    } catch (e) {
      // ignore
    }
    setGateError(msg || "Access key is missing or wrong.");
    setAppKey("");
  }, []);

  if (!appKey) return <KeyGate onSubmit={saveKey} error={gateError} />;

  return (
    <TagsContext.Provider value={tagsValue}>
    <div className="app">
      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand">
            <img className="brand-logo" src={LOGO} alt={LOGO_ALT} />
            <span className="brand-divider" aria-hidden="true" />
            <span className="brand-name">Stop Check</span>
          </div>
          <nav className="tabs" aria-label="Sections">
            {TABS.map((t) => (
              <button
                key={t.id}
                className={`tab ${t.icon ? "tab-icon" : ""} ${tab === t.id ? "tab-active" : ""}`}
                aria-current={tab === t.id ? "page" : undefined}
                aria-label={t.icon ? t.label : undefined}
                title={t.icon ? t.label : undefined}
                onClick={() => setTab(t.id)}
              >
                {t.icon ? <GearIcon /> : t.label}
              </button>
            ))}
          </nav>
          <button className="btn btn-quiet" onClick={() => handleAuthError("Signed out.")}>Sign out</button>
        </div>
      </header>
      <main className="content">
        {tab === "report" ? <ReportView appKey={appKey} onAuthError={handleAuthError} date={reportDate} onDateChange={setReportDate} onOpenSettings={openSettings} /> : null}
        {tab === "status" ? <StatusView appKey={appKey} onAuthError={handleAuthError} onShowReport={showReport} /> : null}
        {tab === "units" ? <UnitsView appKey={appKey} onAuthError={handleAuthError} /> : null}
        {tab === "landmarks" ? <LandmarksView appKey={appKey} onAuthError={handleAuthError} onOpenSettings={openSettings} /> : null}
        {tab === "settings" ? <SettingsView appKey={appKey} onAuthError={handleAuthError} /> : null}
      </main>
    </div>
    </TagsContext.Provider>
  );
}
